/**
 *
 * usePersonalEmail
 *
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import {
  makeSelectAuth,
  makeSelectAuthModal,
  makeSelectProfile,
} from 'containers/Auth/selectors';
import {
  hidePersonalEmailModal,
  showPersonalEmailModal,
} from 'containers/Main/actions';
import {
  makeSelectClientDetails,
  makeSelectPersonalEmailModal,
} from 'containers/Main/selectors';
import useIsAuthPath from './useIsAuthPath';

function usePersonalEmail() {
  const dispatch = useDispatch();
  const auth = useSelector(makeSelectAuth());
  const authModal = useSelector(makeSelectAuthModal());
  const isAuthPath = useIsAuthPath();
  const profile = useSelector(makeSelectProfile());
  const clientDetails = useSelector(makeSelectClientDetails());
  const personalEmailModal = useSelector(makeSelectPersonalEmailModal());
  const personalEmailSkippedAt = personalEmailModal?.personalEmailSkippedAt;

  const isEnabled = _get(clientDetails, 'metadata.requirePersonalEmail', false);
  const showAgainConfig = _get(
    clientDetails,
    'metadata.subscriptionModal.showAgainIfSkipped',
  );
  const currentSessionSec =
    (_get(showAgainConfig, 'currentSessionSec', 0) || 0) * 1000;
  const nextSessionSec =
    (_get(showAgainConfig, 'nextSessionSec', 0) || 0) * 1000;

  const hasAssessedSignUp = useRef(false);
  const rePromptScheduled = useRef(false);

  useEffect(() => {
    const isReady =
      isEnabled &&
      !isAuthPath &&
      profile.isLoaded &&
      !profile.isEmpty &&
      auth.isLoaded &&
      !authModal.show;
    if (!isReady) {
      return () => {};
    }

    if (!_isEmpty(profile.personalEmail)) {
      dispatch(hidePersonalEmailModal());
      return () => {};
    }

    let timerId;

    if (_isEmpty(showAgainConfig)) {
      if (
        Date.now() - auth.createdAt < 5 * 60 * 1000 &&
        !hasAssessedSignUp.current
      ) {
        hasAssessedSignUp.current = true;
        dispatch(showPersonalEmailModal({ type: 'sign-up' }));
      }
    } else {
      const isFirstLogin = auth.createdAt === auth.lastLoginAt;

      if (isFirstLogin) {
        dispatch(showPersonalEmailModal({ type: 'sign-up' }));
      } else {
        timerId = setTimeout(() => {
          dispatch(showPersonalEmailModal({ type: 'sign-up' }));
        }, nextSessionSec);
      }
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [
    auth.isLoaded,
    auth.createdAt,
    auth.lastLoginAt,
    authModal.show,
    isAuthPath,
    isEnabled,
    nextSessionSec,
    profile.isLoaded,
    profile.isEmpty,
    profile.personalEmail,
    showAgainConfig,
    dispatch,
  ]);

  useEffect(() => {
    const isReady =
      isEnabled &&
      !isAuthPath &&
      profile.isLoaded &&
      !profile.isEmpty &&
      auth.isLoaded &&
      !authModal.show;
    const isFirstLogin = auth.createdAt === auth.lastLoginAt;

    if (
      isReady &&
      !_isEmpty(showAgainConfig) &&
      personalEmailSkippedAt &&
      !rePromptScheduled.current &&
      isFirstLogin
    ) {
      rePromptScheduled.current = true;

      const timerId = setTimeout(() => {
        dispatch(showPersonalEmailModal({ type: 'sign-up' }));
      }, currentSessionSec);

      return () => clearTimeout(timerId);
    }
    return () => {};
  }, [
    personalEmailSkippedAt,
    showAgainConfig,
    auth.createdAt,
    auth.lastLoginAt,
    authModal.show,
    currentSessionSec,
    isAuthPath,
    dispatch,
  ]);
}

export default usePersonalEmail;
